import { useState, useMemo, useCallback, useEffect, useRef, Component } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";
import { provideNowInfo } from "../epg/nowInfo.js";
import type { Channel, Program } from "@hybrid-iptv/types";
import { usePlayerStore } from "../player/playerStore.js";
import { announceZap, arrowsBelongToBar, noteZap } from "../player/zapRule.js";
import { useEpgSettings, FONT_SCALE_MAP, GUIDE_REACH_HOURS } from "../epg/settingsStore.js";
import { GUIDE_FIRST_HOURS, mergeProgramIndexes, useChannels, useEpgFor, useGuideEpg, useProgramIndex } from "../epg/hooks.js";
import { EpgToolbar } from "../epg/EpgToolbar.js";
import { GuideLoadingStatus } from "../epg/GuideLoadingStatus.js";
import { OverlayLayout } from "../epg/OverlayLayout.js";
import { TraditionalLayout } from "../epg/TraditionalLayout.js";
import { ProgramDetail } from "../epg/ProgramDetail.js";
import { ProgramContextMenu } from "../epg/ProgramContextMenu.js";
import { RecordDialog } from "../dvr/RecordDialog.js";
import { useDvrSourceSync } from "../dvr/dvrSync.js";
import { MiniEpg } from "../epg/MiniEpg.js";
import { PreviewStatus } from "../epg/PreviewStatus.js";
import { ErrorState } from "@hybrid-iptv/ui";
import { ProviderSetupPrompt, isProviderNotConfigured } from "../components/ProviderSetupPrompt.js";
import { FAVORITES_CATEGORY, useFavoritesStore } from "../library/favoritesStore.js";

/** The guide's pseudo-category listing the hidden channels, where they can be unhidden. */
const HIDDEN_CATEGORY = "__hidden__";
import { useFavoriteRefs, useHiddenChannelIds } from "../library/useFavorites.js";
import { MirrorPicker } from "../library/MirrorPicker.js";
import { ChooseGuideDialog } from "../epg/ChooseGuideDialog.js";
import { nativeGuideAvailable } from "../api/nativeGuide.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { activeProviderSourceId, useActiveProviderSourceId } from "../epg/api.js";
import { openingCategory, orderCategories } from "../epg/categoryOrder.js";
import { useViewerCountry } from "../settings/countryStore.js";
import { useRemoteActions } from "../remote/remoteActions.js";
import { pickStartChannel } from "../startup/pickStartChannel.js";
import type { TvStartState } from "../startup/TvStart.js";
import { TvStartCard, type TvStartCardChannel } from "../startup/TvStartCard.js";
import { requireFeature } from "../library/proFeatures.js";

/** How long the channel keys must be quiet before a zap tunes (a held CH+ flicks through channels first). */
const ZAP_SETTLE_MS = 450;

/** Catches render errors inside the EPG tree without crashing the full app. */
class EpgErrorBoundary extends Component<{ children: React.ReactNode; onRetry: () => void }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="page">
          <ErrorState
            title="EPG unavailable"
            body="Something went wrong loading the guide. The player is still running."
            retryLabel="Retry"
            onRetry={() => {
              this.setState({ error: null });
              this.props.onRetry();
            }}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

export function LivePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const layout = useEpgSettings((s) => s.layout);
  const previewWidthFraction = useEpgSettings((s) => s.previewWidthFraction());
  const fontSize = useEpgSettings((s) => s.fontSize);
  const activeCategoryId = useEpgSettings((s) => s.activeCategoryId);
  const lastChannelId = useEpgSettings((s) => s.lastChannelId);
  const setLastChannelId = useEpgSettings((s) => s.setLastChannelId);
  const setActiveCategory = useEpgSettings((s) => s.setActiveCategory);
  const chooseCategory = useEpgSettings((s) => s.chooseCategory);
  const categoryChosen = useEpgSettings((s) => s.categoryChosen);
  const openingPending = useEpgSettings((s) => s.openingPending);
  const openingDone = useEpgSettings((s) => s.openingDone);
  // "Your country": whose categories come first (epg/categoryOrder.ts).
  const country = useViewerCountry();
  const favoritesHydrated = useFavoritesStore((s) => s.hydrated);
  const playSource = usePlayerStore((s) => s.playSource);
  const pushRecent = usePlayerStore((s) => s.pushRecent);
  const playerSource = usePlayerStore((s) => s.source);
  const playerStatus = usePlayerStore((s) => s.status);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  /** Long-press / hold-OK quick-action menu target (Section 11). */
  const [contextTarget, setContextTarget] = useState<{ program: Program | null; channel: Channel; x: number; y: number; fromChannel?: boolean } | null>(null);
  /** Section 16: the channel being mirrored to a fallback provider. */
  const [mirrorChannel, setMirrorChannel] = useState<Channel | null>(null);
  const [guideChannel, setGuideChannel] = useState<Channel | null>(null);
  const [miniEpgVisible, setMiniEpgVisible] = useState(false);
  const [miniEpgPrograms, setMiniEpgPrograms] = useState<Program[]>([]);
  const [miniEpgChannel, setMiniEpgChannel] = useState<Channel | null>(null);

  // The active provider's id, as Home, Search and the background sync use.
  // This page used a fixed "default" from before multi-source, so it asked for
  // the SAME provider's channels and guide under a second id: a second copy of
  // each in the query and device caches, and a second guide download and parse
  // (an 85 MB feed on the server; on the phone the two ids evicted each other).
  const sourceId = useActiveProviderSourceId();
  const {
    data: channelData,
    error: channelsError,
    refetch: refetchChannels,
  } = useChannels(sourceId);

  const channels = channelData?.channels ?? [];

  // Favorites / hide-show (Section 11): the guide gets a ★ Favorites category
  // showing only starred channels, and hidden channels stay out of EVERY guide
  // view — that is what makes "hide" mean something on a 10k-channel grid.
  const favoriteChannelRefs = useFavoriteRefs("channel");
  const favoriteChannelIds = useMemo(() => new Set(favoriteChannelRefs), [favoriteChannelRefs]);
  const hiddenChannelIds = useHiddenChannelIds();
  const hiddenChannelCount = useMemo(() => channels.filter((c) => hiddenChannelIds.has(c.id)).length, [channels, hiddenChannelIds]);

  const groups: Map<string, Channel[]> = channelData?.groups ?? new Map();
  const [recordTarget, setRecordTarget] = useState<{ program: Program; channel: Channel } | null>(null);

  // Channels shown in the guide: the active category's channels (or all when
  // the dropdown is on "All categories"). Categories stay out of the guide so
  // the EPG only ever deals with a bounded channel set. The ★ Favorites
  // pseudo-category shows starred channels only; hidden channels are filtered
  // from every view.
  const guideChannels = useMemo(() => {
    // The hidden channels themselves, so each can be unhidden (long press →
    // Unhide). Hidden rows were out of every view, the menu with them, so a
    // hidden channel could never come back (projector audit, 2026-09-26).
    if (activeCategoryId === HIDDEN_CATEGORY) return channels.filter((c) => hiddenChannelIds.has(c.id));
    const visible = channels.filter((c) => !hiddenChannelIds.has(c.id));
    if (activeCategoryId === FAVORITES_CATEGORY) {
      return visible.filter((c) => favoriteChannelIds.has(c.id));
    }
    if (activeCategoryId) {
      return (groups.get(activeCategoryId) ?? []).filter((c) => !hiddenChannelIds.has(c.id));
    }
    return visible;
  }, [activeCategoryId, groups, channels, favoriteChannelIds, hiddenChannelIds]);

  // The guide asks for the category on screen only (Phase 3 step 2a), and
  // separately for the channel selected or playing when it is not in it (TV
  // start's channel, one picked from Recent channels), so the zap banner, the
  // info card and Info have its programmes while the grid keeps its own.
  const extraChannels = useMemo(() => {
    const inGuide = new Set(guideChannels.map((c) => c.id));
    const extra: Channel[] = [];
    const add = (channel: Channel | undefined) => {
      if (channel && !inGuide.has(channel.id) && !extra.includes(channel)) extra.push(channel);
    };
    if (selectedChannelId) add(channels.find((c) => c.id === selectedChannelId));
    if (playerSource?.type === "live" && playerSource.title) add(channels.find((c) => c.name === playerSource.title));
    return extra;
  }, [guideChannels, channels, selectedChannelId, playerSource?.type, playerSource?.title]);
  const {
    data: epgData,
    refetch: refetchEpg,
  } = useGuideEpg(sourceId, channels, guideChannels, GUIDE_REACH_HOURS);
  const { data: extraEpgData } = useEpgFor(sourceId, channels, extraChannels, GUIDE_FIRST_HOURS, {
    enabled: extraChannels.length > 0,
    keepPrevious: true,
  });
  // Compute index in useMemo so it re-maps when channels stream in —
  // React Query's select only re-runs when query data changes, not when
  // the name→ID map (captured in the closure) grows.
  const gridIndex = useProgramIndex(epgData, guideChannels);
  const extraIndex = useProgramIndex(extraChannels.length > 0 ? extraEpgData : undefined, extraChannels);
  const programIndex = useMemo(() => mergeProgramIndexes(gridIndex, extraIndex), [gridIndex, extraIndex]);
  // Teaches the Android app's DVR this guide, for series recording on its own.
  useDvrSourceSync(sourceId, epgData, channels);

  // The single <video> lives in the always-mounted player surface. On the
  // Live page the surface is repositioned over the traditional preview panel
  // (or behind the overlay grid) via body[data-epg-layout]; the preview WIDTH
  // fraction is published as a CSS variable and the panel height is derived
  // from it at 16:9 (see --epg-preview-h in app.css), so the surface matches
  // the panel exactly and the video fills it without letterboxing.
  // The shared EPG font size is published the same way (Section 8 step 3).
  useEffect(() => {
    document.documentElement.style.setProperty("--epg-preview-frac", String(previewWidthFraction));
    document.documentElement.style.setProperty("--epg-font-scale", String(FONT_SCALE_MAP[fontSize]));
    document.body.dataset.epgLayout = layout;
    return () => {
      delete document.body.dataset.epgLayout;
      document.documentElement.style.removeProperty("--epg-preview-frac");
      document.documentElement.style.removeProperty("--epg-font-scale");
    };
  }, [layout, previewWidthFraction, fontSize]);

  // The guide is the one screen with somewhere for a picture to go other than
  // full screen, so it says so for as long as it is mounted. Leaving takes the
  // preview pane with it, and PlayerSurface moves the video out rather than
  // parking it in a corner of the next screen. Mount-only on purpose: the
  // effect above re-runs on a layout or font change, which must not read as
  // the pane disappearing.
  useEffect(() => {
    usePlayerStore.getState().setPreviewHost(true);
    return () => usePlayerStore.getState().setPreviewHost(false);
  }, []);

  // The traditional-layout video pane is a fixed element pinned below the
  // toolbar. --epg-toolbar-h was never published, so it fell back to a
  // hardcoded 49px — but .epg-toolbar-controls wraps to a second row on
  // narrow viewports (measured 88px at 622px wide), and the video then drew
  // over the Category dropdown and the channel keypad. Measure and publish it.
  useEffect(() => {
    const toolbar = document.querySelector<HTMLElement>(".epg-toolbar");
    if (!toolbar) return;
    const publish = () => {
      const height = Math.round(toolbar.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--epg-toolbar-h", `${height}px`);
    };
    publish();
    // jsdom (tests) has no ResizeObserver; the one-shot publish above is enough there.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(publish);
    observer.observe(toolbar);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--epg-toolbar-h");
    };
  }, [layout]);

  // Sync selected channel from what's actually playing (survives navigation
  // and category switches, which clear the selection). Depends on
  // selectedChannelId so clearing it (e.g. on category change) re-restores it
  // while a channel is still playing.
  useEffect(() => {
    if (playerSource?.title && channels.length > 0 && !selectedChannelId) {
      const match = channels.find((c) => c.name === playerSource.title);
      if (match) setSelectedChannelId(match.id);
    }
  }, [playerSource?.title, channels.length, selectedChannelId]);

  // The guide's save state (owner, 2026-09-25): back on the guide with nothing
  // playing, reopen on the channel last previewed here and preview it again —
  // the same category comes back on its own (epg-settings). Once per visit,
  // and only when that channel is in the category on screen; with something
  // already playing, the effect above selects that instead.
  //
  // A TV-style start (startup/TvStart.tsx) arrives with a marker in the location state: the
  // restore above is skipped, since the start channel is picked from the profile's recents
  // first (below), and the marker is cleared at once so a later visit is an ordinary one.
  const tvStartAsked = (location.state as Partial<TvStartState> | null)?.tvStart === true;
  const restoredPreview = useRef(tvStartAsked);
  const tvStart = useRef<{ phase: "idle" | "waiting" | "category" | "tune" | "done"; channel: Channel | null }>({
    phase: tvStartAsked ? "waiting" : "idle",
    channel: null,
  });
  // The starting card (startup/TvStartCard.tsx) is on from the first frame of a TV-style start.
  const [tvCard, setTvCard] = useState<{ channel: TvStartCardChannel | null } | null>(
    tvStartAsked ? { channel: null } : null,
  );
  useEffect(() => {
    if (!tvStartAsked) return;
    navigate(location.pathname, { replace: true, state: null });
    // Never a blank screen: past 10 s the viewer simply stays on the guide.
    const timer = window.setTimeout(() => {
      if (tvStart.current.phase === "waiting" || tvStart.current.phase === "category") {
        tvStart.current.phase = "done";
        setTvCard(null);
      }
    }, 10_000);
    return () => window.clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The card is removed when the picture is up in full screen. Any button press removes it at once and, before the tune,
  // cancels the jump: the viewer chose to use the guide.
  useEffect(() => {
    if (!tvCard) return;
    const onKey = () => {
      setTvCard(null);
      const phase = tvStart.current.phase;
      if (phase === "waiting" || phase === "category") tvStart.current.phase = "done";
    };
    // Capture and passive: the press still does its normal job underneath.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [tvCard]);
  // Gone when the picture plays in full screen, or the player fails; looked at every 250 ms
  // because the picture and the full-screen layer arrive in either order.
  useEffect(() => {
    if (!tvCard?.channel) return;
    const timer = window.setInterval(() => {
      const { status } = usePlayerStore.getState();
      const full = document.documentElement.classList.contains("player-fullscreen");
      if (status === "error" || (status === "playing" && full)) setTvCard(null);
    }, 250);
    return () => window.clearInterval(timer);
  }, [tvCard]);

  // Category display names — derived from category IDs
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id] of groups) {
      // Make category IDs human-readable
      map.set(id, id.replace(/^cat-/, "").replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()));
    }
    return map;
  }, [groups]);

  // Held on a channel row: the programme menu for what is on now, with Record
  // greyed out — a recording is of a programme, and none was picked.
  const openChannelMenu = useCallback(
    (channel: Channel, point: { x: number; y: number }) =>
      setContextTarget({ program: programIndex?.getNowPlaying(channel.id) ?? null, channel, x: point.x, y: point.y, fromChannel: true }),
    [programIndex],
  );

  const handleChannelSelect = useCallback(
    async (channel: Channel, opts?: { toast?: boolean; program?: Program; tune?: boolean }) => {
      // Never with the picture full screen: the card sits under it, unseen, and
      // still held a Back step — after a zap the first Back closed a card no one
      // could see and did nothing visible (projector, 2026-09-26). The full-screen
      // bar already names the channel.
      const showToast = opts?.toast !== false && !document.querySelector(".player-surface--fullscreen");
      setSelectedChannelId(channel.id);

      // Raise the detail card IMMEDIATELY on select — cable-like instant
      // feedback while the stream loads in the background. This now happens
      // ONLY for an explicit act (OK/Enter or a click on a row or a programme),
      // never for merely highlighting one while scrolling. Passing `program`
      // describes the block the viewer picked; without it the card shows
      // now/next. Fullscreen paths (Go Live, the detail dialog's Play) suppress
      // it — there is nothing left to decide once the picture is up.
      if (showToast && programIndex) {
        const allPrograms = programIndex.getProgramsForChannel(channel.id);
        const nowMs = Date.now();
        let base = allPrograms.findIndex(
          (p) => nowMs >= Date.parse(p.start) && nowMs < Date.parse(p.end),
        );
        // Nothing listed at this minute (a gap in the provider's feed — common
        // on the movie/per-event channels): describe what is UP NEXT. Falling
        // back to the top of the day listed the 2am film as if it were on now.
        if (base < 0) base = allPrograms.findIndex((p) => Date.parse(p.start) > nowMs);
        const slice = opts?.program
          ? [opts.program]
          : base >= 0
            ? allPrograms.slice(base, base + 3)
            : allPrograms.slice(0, 3);
        if (slice.length > 0) {
          setMiniEpgPrograms(slice);
          setMiniEpgChannel(channel);
          setMiniEpgVisible(true);
        }
      }

      // A held CH+/CH− moves through channels on every repeat but tunes only
      // the one the viewer stops on (the quick-zap below): the card follows
      // along, the stream and the history wait.
      if (opts?.tune === false) return;
      // What the guide reopens on next time (the save state below).
      setLastChannelId(channel.id);

      // Record in the dock's recent-history so Previous-channel and the
      // fullscreen strip can retune without re-querying the catalog.
      pushRecent({
        id: channel.id,
        name: channel.name,
        number: channel.number,
        logoUrl: channel.logoUrl,
        streamUrl: channel.streamUrl,
        altStreamUrls: channel.altStreamUrls,
      });

      // Already WATCHING this channel: picking another programme on it updates
      // the card, it must not reload the feed from live (and burn a connection).
      // An error or an ended stream is NOT "already watching" — re-picking the
      // channel is how the viewer retries it, so that must load again.
      const alreadyTuned =
        playerSource?.type === "live" &&
        playerSource.title === channel.name &&
        playerStatus !== "error" &&
        playerStatus !== "idle" &&
        playerStatus !== "ended";
      if (alreadyTuned) return;

      try {
        await playSource({
          url: channel.streamUrl,
          type: "live",
          title: channel.name,
          altUrls: channel.altStreamUrls,
          // Cross-provider mirror fallbacks (Section 16).
          mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id),
          // Selecting a channel row previews it beside the guide so browsing
          // carries on; Go Live is the watch action and fills the screen.
          preview: true,
        });
      } catch {
        // Player handles its own error states — no additional UI needed here
      }
    },
    [playSource, pushRecent, programIndex, playerSource, playerStatus, setLastChannelId],
  );

  useEffect(() => {
    if (restoredPreview.current || guideChannels.length === 0) return;
    restoredPreview.current = true;
    if (playerSource) return;
    const channel = lastChannelId ? guideChannels.find((c) => c.id === lastChannelId) : undefined;
    if (channel) void handleChannelSelect(channel, { toast: false });
  }, [guideChannels, playerSource, lastChannelId, handleChannelSelect]);

  // Overlay mode: OK/Enter on a programme is NOT "watch now" any more — it
  // tunes the channel BEHIND the guide and raises the detail card for that
  // programme, focused, so Go Live on the card is one D-pad press away. The
  // guide is for browsing: highlighting used to tune and cover the grid, and
  // the card's own buttons were unreachable from a remote (owner, 2026-09-21).
  // The app's own full screen, not the browser's: inside the APK the browser's
  // puts the page in a layer ABOVE the native video, which shows black. Back
  // leaves it and lands on this page again, so nobody is stuck (owner,
  // 2026-09-20).
  const requestFullscreen = usePlayerStore((s) => s.requestFullscreen);
  const goFullscreen = useCallback(() => requestFullscreen(), [requestFullscreen]);

  // The TV-style start: pick the channel, bring its category up if the guide opened on another,
  // tune it and take it full screen. It waits for the guide's own opening category to be decided
  // (openingPending), or that logic would switch the category under it.
  useEffect(() => {
    const run = tvStart.current;
    if (run.phase !== "waiting" && run.phase !== "category" && run.phase !== "tune") return;
    if (channels.length === 0 || openingPending) return;
    if (run.phase === "waiting") {
      const channel = pickStartChannel({
        recentIds: usePlayerStore.getState().recentChannels.map((c) => c.id),
        lastChannelId: useEpgSettings.getState().lastChannelId,
        channels: channels.filter((c) => !hiddenChannelIds.has(c.id)),
      });
      if (!channel) {
        run.phase = "done";
        setTvCard(null);
        return;
      }
      run.channel = channel;
      setTvCard({ channel: { name: channel.name, number: channel.number, logoUrl: channel.logoUrl } });
      if (!guideChannels.some((c) => c.id === channel.id)) {
        // Not the viewer's own choice of category, so it is not remembered as one.
        setActiveCategory(channel.categoryId);
        run.phase = "category";
        return;
      }
      run.phase = "tune";
    }
    if (run.phase === "category" && run.channel && guideChannels.some((c) => c.id === run.channel!.id)) run.phase = "tune";
    if (run.phase === "tune" && run.channel) {
      const channel = run.channel;
      run.phase = "done";
      void (async () => {
        await handleChannelSelect(channel, { toast: false });
        requestFullscreen();
      })();
    }
  }, [channels, openingPending, guideChannels, hiddenChannelIds, setActiveCategory, handleChannelSelect, requestFullscreen]);

  // OK on a channel in the guide. The first OK tunes it and raises its card,
  // whose Go Live is the way to full screen. A channel with nothing listed has
  // no card, so a second OK on it — already playing — did nothing at all: a
  // dead press on every channel without a guide, and on all of them while a
  // fresh install is still loading the guide (projector, 2026-09-27). Then OK
  // goes full screen, as it does on TiviMate.
  const handleChannelRowSelect = useCallback(
    (channel: Channel) => {
      const playing =
        playerSource?.type === "live" &&
        playerSource.title === channel.name &&
        playerStatus !== "error" &&
        playerStatus !== "idle" &&
        playerStatus !== "ended";
      const hasCard = (programIndex?.getProgramsForChannel(channel.id).length ?? 0) > 0;
      if (playing && !hasCard) {
        setSelectedChannelId(channel.id);
        goFullscreen();
        return;
      }
      void handleChannelSelect(channel);
    },
    [playerSource, playerStatus, programIndex, handleChannelSelect, goFullscreen],
  );

  const handleProgramSelect = useCallback(
    (program: Program, channel: Channel) => {
      setSelectedChannelId(channel.id);
      if (layout === "overlay") {
        setSelectedProgram(null);
        void handleChannelSelect(channel, { program });
      } else {
        // Traditional layout: the detail popup IS the confirmation step, so the
        // preview starts WITH it — that is why there is no Preview button on it
        // any more (owner, 2026-09-21). Its Watch is the fullscreen action. The
        // card is suppressed: this popup already says everything it would.
        void handleChannelSelect(channel, { toast: false });
        setSelectedProgram(program);
      }
    },
    [layout, handleChannelSelect],
  );

  const handlePlayFromDetail = useCallback(() => {
    if (!selectedProgram || !selectedChannelId) return;
    const channel = channels.find((c) => c.id === selectedChannelId);
    if (channel) {
      setSelectedProgram(null);
      setMiniEpgVisible(false);
      void handleChannelSelect(channel, { toast: false });
      goFullscreen();
    }
  }, [selectedProgram, selectedChannelId, channels, handleChannelSelect, goFullscreen]);

  const handleRefresh = useCallback(() => {
    refetchChannels();
    refetchEpg();
  }, [refetchChannels, refetchEpg]);

  const handleZapTo = useCallback(
    (channel: Channel) => {
      handleChannelSelect(channel);
    },
    [handleChannelSelect],
  );

  const zapTimer = useRef<number | null>(null);

  /** One step through the guide's channels; false when there is nothing to step through. */
  const zap = useCallback(
    (next: boolean): boolean => {
      if (guideChannels.length === 0) return false;
      const currentIdx = selectedChannelId
        ? guideChannels.findIndex((c) => c.id === selectedChannelId)
        : -1;
      const nextIdx = next
        ? (currentIdx + 1) % guideChannels.length
        : (currentIdx <= 0 ? guideChannels.length : currentIdx) - 1;
      const channel = guideChannels[nextIdx]!;
      // Move and show at once; tune once the keys go quiet. WHY (2026-09-25):
      // a held CH+ tuned on every repeat — a new stream, a provider connection
      // and a redrawn card several times a second.
      void handleChannelSelect(channel, { tune: false });
      announceZap(channel.name);
      if (zapTimer.current !== null) window.clearTimeout(zapTimer.current);
      zapTimer.current = window.setTimeout(() => {
        zapTimer.current = null;
        void handleChannelSelect(channel);
      }, ZAP_SETTLE_MS);
      return true;
    },
    [guideChannels, selectedChannelId, handleChannelSelect],
  );

  // The remote's Info button in full screen asks here what is on (epg/nowInfo.ts).
  useEffect(
    () =>
      provideNowInfo((name) => {
        const channel = channels.find((c) => c.name === name) ?? channels.find((c) => c.id === selectedChannelId);
        if (!channel) return null;
        const now = new Date();
        const programs = programIndex?.getProgramsInWindow(channel.id, now, new Date(now.getTime() + 12 * 3600_000)) ?? [];
        const at = programs.findIndex((p) => Date.parse(p.start) <= now.getTime() && Date.parse(p.end) > now.getTime());
        return {
          channel,
          now: at >= 0 ? programs[at]! : null,
          next: programs.find((p) => Date.parse(p.start) > now.getTime()) ?? null,
        };
      }),
    [channels, selectedChannelId, programIndex],
  );

  // Quick-zap (Section 7 step 5): ArrowUp/ArrowDown moves through the guide
  // channels relative to the selected one and tunes with the mini-EPG toast.
  // Ignored while the user is typing in any input/select (keypad, dropdowns,
  // search) so arrow keys keep their native behavior there.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // A TV remote's CH+/CH− zap from anywhere; the arrows only when no
      // control has focus (below).
      const channelKey = e.key === "ChannelUp" || e.key === "ChannelDown";
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && !channelKey) return;
      const target = e.target;
      // e.target can be an Element (real keypresses) or the window itself
      // (synthetic/test dispatches) — only apply element checks when it's one.
      const isTyping = target instanceof HTMLElement
        && (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isTyping) return;
      // Don't zap while a real dialog is open (CH keypad, program detail) — the
      // focus sits on its buttons and arrows should stay inside the dialog.
      // Also skip when focus is inside the guide: the guide's roving D-pad
      // focus owns ArrowUp/Down there (it only moves the ring — see
      // guideFocus.ts). The mini-EPG card is NOT modal for the channel keys:
      // CH+/− still zaps behind it, so it stays a live preview of whatever is
      // tuned (the card is focused, which is why the exception is needed).
      const dialog = target instanceof Element ? target.closest('[role="dialog"]') : null;
      const modalDialog = dialog !== null && !dialog.hasAttribute("data-epg-mini");
      if (target instanceof Element && (modalDialog || target.closest("[data-guide]"))) return;
      // With focus on any other control — the nav rail, the toolbar, the
      // player's buttons — the arrows move focus (components/spatialNav.ts).
      // On a TV remote, Down in the nav rail used to change the channel.
      if (!channelKey && target instanceof HTMLElement && target !== document.body) return;
      // The arrows zap only while the picture fills the screen. Beside the guide
      // they never tune — tuning there is OK on a row or programme (2026-09-21).
      // WHY (2026-09-25): with the detail card open and focus lost to <body>,
      // every Down zapped, and the redrawn card lost focus again, so the viewer
      // was locked into re-tuning on every press ("it wreaks havoc on the
      // guide"). guideFocus.ts hands a stray arrow back to the guide or the card.
      if (!channelKey && !document.querySelector(".player-surface--fullscreen")) return;
      // The bar is up: Up/Down go into it, they do not change the channel (player/zapRule.ts).
      if (!channelKey && arrowsBelongToBar(e)) return;
      if (zap(e.key === "ArrowDown" || e.key === "ChannelUp")) {
        noteZap();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [zap]);

  // Remote buttons mapped to a channel change or to programme info
  // (remote/remoteMap.ts) run the same code as CH+/CH− and the card.
  useRemoteActions({
    channelNext: () => {
      noteZap();
      return zap(true);
    },
    channelPrev: () => {
      noteZap();
      return zap(false);
    },
    info: () => {
      // Full screen, the player's bar answers (PlayerSurface).
      if (document.querySelector(".player-surface--fullscreen")) return false;
      const channel = guideChannels.find((c) => c.id === selectedChannelId);
      if (!channel) return false;
      void handleChannelSelect(channel, { tune: false });
    },
  });
  // A zap still waiting to tune when the page goes away is dropped.
  useEffect(() => () => {
    if (zapTimer.current !== null) window.clearTimeout(zapTimer.current);
  }, []);

  // The category before "Hidden channels", to go back to once none are left.
  const beforeHidden = useRef<string | null>(null);
  const handleSelectCategory = useCallback(
    (id: string | null) => {
      if (id === HIDDEN_CATEGORY && activeCategoryId !== HIDDEN_CATEGORY) beforeHidden.current = activeCategoryId;
      chooseCategory(id);
      setSelectedChannelId(null);
    },
    [chooseCategory, activeCategoryId],
  );
  // A viewer who never picked a category starts on the first in the list (USA
  // Ultra HD first, orderCategories), not "All categories": every channel at
  // once is heavy on a 1 GB box and a poor first look (fresh install on the
  // projector, 2026-09-27). Not remembered as a choice, so the list order can
  // still change it until the viewer picks one.
  useEffect(() => {
    if (openingPending || categoryChosen || activeCategoryId !== null || groups.size === 0) return;
    const first = orderCategories([...groups.keys()].map((id) => ({ id, name: categoryNames.get(id) ?? id })), country)[0]?.id;
    if (first) setActiveCategory(first);
  }, [openingPending, categoryChosen, activeCategoryId, groups, categoryNames, setActiveCategory, country]);
  // At app start and after a profile switch the guide opens on the profile's
  // ★ Favorites when it has any (owner, 2026-09-29), else as above. Waits for
  // the profile's favourites and the channel list, so it decides once, right.
  useEffect(() => {
    if (!openingPending || !favoritesHydrated || groups.size === 0) return;
    const next = openingCategory({
      favoritesId: FAVORITES_CATEGORY,
      favoriteCount: channels.filter((c) => favoriteChannelIds.has(c.id) && !hiddenChannelIds.has(c.id)).length,
      saved: activeCategoryId,
      chosen: categoryChosen,
      savedExists: activeCategoryId === HIDDEN_CATEGORY || (activeCategoryId !== null && groups.has(activeCategoryId)),
      first: orderCategories([...groups.keys()].map((id) => ({ id, name: categoryNames.get(id) ?? id })), country)[0]?.id ?? null,
    });
    if (next !== activeCategoryId) setActiveCategory(next);
    openingDone();
  }, [openingPending, favoritesHydrated, groups, channels, favoriteChannelIds, hiddenChannelIds, activeCategoryId, categoryChosen, categoryNames, setActiveCategory, openingDone, country]);
  // The last hidden channel was unhidden: the list is gone, so back to where the viewer was.
  // After a restart there is no "before", so the first category in the list
  // (USA Ultra HD first, orderCategories) — never "All categories", which is
  // every channel at once and heavy on a 1 GB box.
  useEffect(() => {
    if (activeCategoryId === HIDDEN_CATEGORY && hiddenChannelCount === 0 && channels.length > 0) {
      const first = orderCategories([...groups.keys()].map((id) => ({ id, name: categoryNames.get(id) ?? id })), country)[0]?.id ?? null;
      setActiveCategory(beforeHidden.current ?? first);
    }
  }, [activeCategoryId, hiddenChannelCount, channels.length, setActiveCategory, groups, categoryNames, country]);

  // Category options for the toolbar dropdown — ★ Favorites first, then the
  // provider's categories, USA first (orderCategories). The favorites count
  // reflects what the guide will actually show (starred minus hidden).
  const categoryOptions = useMemo(
    () => [
      {
        id: FAVORITES_CATEGORY,
        name: "★ Favorites",
        count: channels.filter((c) => favoriteChannelIds.has(c.id) && !hiddenChannelIds.has(c.id)).length,
      },
      ...orderCategories([...groups.entries()].map(([id, groupChannels]) => ({
        id,
        name: categoryNames.get(id) ?? id,
        count: groupChannels.length,
      })), country),
      // Last, and only while something is hidden: where hidden channels come back.
      ...(hiddenChannelCount > 0 ? [{ id: HIDDEN_CATEGORY, name: "🚫 Hidden channels", count: hiddenChannelCount }] : []),
    ],
    [groups, categoryNames, channels, favoriteChannelIds, hiddenChannelIds, hiddenChannelCount, country],
  );

  // Error state — first-visit users see a setup prompt; other errors use
  // the generic state so the user can retry.
  if (channelsError && channels.length === 0) {
    if (isProviderNotConfigured(channelsError)) {
      return <ProviderSetupPrompt fullPage />;
    }
    return (
      <div className="page">
        <ErrorState
          title="Could not load channels"
          body="Check your source connection in Settings."
          retryLabel="Retry"
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className="page page--live">
      <EpgToolbar
        categories={categoryOptions}
        activeCategoryId={activeCategoryId}
        onSelectCategory={handleSelectCategory}
        channels={guideChannels}
        onZapTo={handleZapTo}
      />
      <GuideLoadingStatus />

      <EpgErrorBoundary onRetry={handleRefresh}>
        {layout === "overlay" ? (
        <OverlayLayout
          channels={guideChannels}
          selectedChannelId={selectedChannelId}
          programIndex={programIndex}
          onChannelSelect={handleChannelRowSelect}
          onProgramSelect={handleProgramSelect}
          onProgramContext={(program, channel, point) =>
            setContextTarget({ program, channel, x: point.x, y: point.y })
          }
          onChannelContext={openChannelMenu}
        />
      ) : (
        <TraditionalLayout
          channels={guideChannels}
          allChannels={channels}
          selectedChannelId={selectedChannelId}
          programIndex={programIndex}
          onChannelSelect={handleChannelRowSelect}
          onProgramSelect={handleProgramSelect}
          onProgramContext={(program, channel, point) =>
            setContextTarget({ program, channel, x: point.x, y: point.y })
          }
          onChannelContext={openChannelMenu}
        />        )}
      </EpgErrorBoundary>

      {/* Over the picture, not in the dock: the pane draws no toolbar, so this
          is the only place a viewer can see a channel still loading or read why
          it never came up (no buttons — see PreviewStatus). */}
      <PreviewStatus />

      {/* The popups are drawn on <body>, outside .app: while a video shows,
          .app has a window cut where the picture is (app.css, "Native video"),
          and it cut through every popup over the preview too — the Go Live card
          lost its title and time, the long-press menu its top half (onn box,
          2026-10-06). Out here they sit on top of the picture, whole. */}
      {createPortal(<>
      {selectedProgram && channels.length > 0 && (
        <ProgramDetail
          program={selectedProgram}
          channel={channels.find((c) => c.id === selectedChannelId) ?? channels[0]!}
          onClose={() => setSelectedProgram(null)}
          onPlay={handlePlayFromDetail}
          onRecord={() => {
            const channel = channels.find((c) => c.id === selectedChannelId) ?? channels[0]!;
            if (!requireFeature("recordings")) return;
            setRecordTarget({ program: selectedProgram, channel });
            setSelectedProgram(null);
          }}
        />
      )}

      {contextTarget && (
        <ProgramContextMenu
          program={contextTarget.program}
          channel={contextTarget.channel}
          x={contextTarget.x}
          y={contextTarget.y}
          onClose={() => setContextTarget(null)}
          onPlay={() => handleChannelSelect(contextTarget.channel)}
          fromChannel={contextTarget.fromChannel}
          onDetails={() => {
            if (!contextTarget.program) return;
            setSelectedChannelId(contextTarget.channel.id);
            setSelectedProgram(contextTarget.program);
          }}
          onMirror={() => setMirrorChannel(contextTarget.channel)}
          {...(nativeGuideAvailable() ? { onChooseGuide: () => setGuideChannel(contextTarget.channel) } : {})}
          onRecord={() => {
            if (contextTarget.program && requireFeature("recordings")) {
              setRecordTarget({ program: contextTarget.program, channel: contextTarget.channel });
            }
          }}
        />
      )}

      {recordTarget && (
        <RecordDialog program={recordTarget.program} channel={recordTarget.channel} onClose={() => setRecordTarget(null)} />
      )}

      {guideChannel && (
        <ChooseGuideDialog sourceId={activeProviderSourceId()} channel={guideChannel} onClose={() => setGuideChannel(null)} />
      )}

      {mirrorChannel && (
        <MirrorPicker
          open
          sourceId={activeProviderSourceId()}
          channel={mirrorChannel}
          onClose={() => setMirrorChannel(null)}
        />
      )}

      <MiniEpg
        channel={miniEpgChannel}
        programs={miniEpgPrograms}
        visible={miniEpgVisible}
        onClose={() => setMiniEpgVisible(false)}
        onGoLive={() => {
          setMiniEpgVisible(false);
          goFullscreen();
        }}
        onRecord={
          // Android only: the card's Record opens the same DVR dialog the
          // traditional layout's popup does, and hands focus to it.
          miniEpgChannel
            ? (program) => {
                if (!requireFeature("recordings")) return;
                setRecordTarget({ program, channel: miniEpgChannel });
                setMiniEpgVisible(false);
              }
            : undefined
        }
      />
      </>, document.body)}
      {/* On <body>, like the popups above: in full screen everything inside .app is hidden. */}
      {tvCard && createPortal(<TvStartCard channel={tvCard.channel} />, document.body)}
    </div>
  );
}
